/**
 * Refresh Stage 12: WordPress Publish (Update in-place)
 *
 * Updates the existing WordPress post with refreshed content. Post stays
 * at status: publish throughout. Uses refresh.source_wp_id to identify
 * the existing post.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { EditorialReviewBanner } from './EditorialReviewBanner';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const seo = document?.article?.seo;
  const category = document?.thesis?.wordpress_category;
  const markdown = document?.article?.markdown;
  const refresh = (document as any)?.refresh;
  const wp = document?.preflight?.wordpress;

  const sourceWpId = refresh?.source_wp_id;
  const sourceUrl = refresh?.source_url;

  const warnings: string[] = [];
  if (!sourceWpId) warnings.push('No source_wp_id — WordPress post could not be identified. Try re-creating this refresh run.');
  if (!markdown) warnings.push('No article markdown — run earlier stages first.');
  if (!category) warnings.push('No WordPress category set on candidate.');

  return (
    <div className="space-y-3">
      <EditorialReviewBanner />
      <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
        <div className="font-medium text-blue-800">
          {sourceWpId ? `Target: Existing WP post #${sourceWpId}` : 'Target: Will look up post by URL'}
        </div>
        {sourceUrl && (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {sourceUrl}
          </a>
        )}
        <div className="text-blue-700 mt-1">
          This will UPDATE the live post in-place — status stays "publish."
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-gray-500">Category:</span>{' '}
          <span className="text-gray-700">{category?.name || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Slug:</span>{' '}
          <span className="text-gray-700">{seo?.slug || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Meta title:</span>{' '}
          <span className="text-gray-700">{seo?.meta_title || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Meta description:</span>{' '}
          <span className="text-gray-700">{seo?.meta_description || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Article length:</span>{' '}
          <span className="text-gray-700">
            {markdown ? `${markdown.split(/\s+/).filter(Boolean).length} words` : '—'}
          </span>
        </div>
      </div>
      {warnings.length > 0 && (
        <div className="space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {w}
            </div>
          ))}
        </div>
      )}
      {wp && (
        <div className="text-xs bg-green-50 border border-green-200 rounded px-2 py-1">
          Last updated: Post #{wp.post_id || (wp as any).wordpress_post_id}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const wp = doc.preflight?.wordpress;
  const postId = wp?.post_id || (wp as any)?.wordpress_post_id;
  if (!wp || !postId) {
    return <div className="text-sm text-gray-500">Not updated yet.</div>;
  }
  const postUrl = wp.post_url || (wp as any).wordpress_post_url;
  const postStatus = wp.post_status || (wp as any).status;

  return (
    <div className="space-y-2">
      <div className="text-xs">
        <span className="font-medium text-gray-700">Post ID:</span>{' '}
        <span className="text-gray-600">{postId}</span>
      </div>
      <div className="text-xs">
        <span className="font-medium text-gray-700">Status:</span>{' '}
        <span className="text-gray-600">{postStatus}</span>
      </div>
      {postUrl && (
        <div className="text-xs">
          <span className="font-medium text-gray-700">URL:</span>{' '}
          <a href={postUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {postUrl}
          </a>
        </div>
      )}
    </div>
  );
}

export function RefreshPublishV2() {
  const { document } = useArticleContext();
  const hasMarkdown = Boolean(document?.article?.markdown);
  const hasWpId = Boolean((document as any)?.refresh?.source_wp_id);

  return (
    <StagePanel
      stageId="refresh_12_publish"
      title="Refresh: Publish to WordPress"
      description="Updates the existing WordPress post with refreshed content. Keeps publish status — no revert to draft."
      runDisabled={!hasMarkdown || !hasWpId}
      runDisabledReason={
        !hasMarkdown ? 'No article markdown yet' :
        !hasWpId ? 'No source_wp_id — re-create this refresh run to fix' :
        undefined
      }
      runLabel="Update live WordPress post"
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
